import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const lesVoilesDeSaintTropezEn: ChauffeurPage = {
  slug: "les-voiles-de-saint-tropez",
  title: "Private chauffeur Les Voiles de Saint-Tropez – VTC",
  subtitle:
    "Private chauffeur during Les Voiles de Saint-Tropez: access to the old port despite the peninsula's traffic jams, airport transfers and evenings.",
  keywords: [
    "les voiles de saint-tropez chauffeur",
    "saint-tropez regatta chauffeur",
    "saint-tropez private driver",
  ],
  hero: {
    title: "Private chauffeur during Les Voiles de Saint-Tropez",
    subtitle:
      "Regattas of traditional yachts in the gulf: a dedicated chauffeur when the peninsula fills up and the old port becomes impossible to reach by car.",
    keywords: [
      "Les Voiles de Saint-Tropez chauffeur",
      "Saint-Tropez old port VTC",
      "Nice airport Saint-Tropez transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during Les Voiles de Saint-Tropez",
    badges: ["Airport transfer", "Old-port access", "Evenings & returns"],
  },
  benefits: {
    heading: "Why a chauffeur during Les Voiles",
    items: [
      {
        title: "Congested peninsula handled",
        description:
          "In late September, the single road into Saint-Tropez is saturated. The chauffeur knows the timings and access points to get you close to the old port.",
      },
      {
        title: "Nice airport transfer",
        description:
          "Nice Côte d'Azur is around a hundred kilometres away. Flight tracking, name-board welcome and a direct ride to your peninsula hotel.",
      },
      {
        title: "Impossible parking solved",
        description:
          "Parking near the port during the regattas is a feat: the chauffeur drops you as close as possible and leaves — you never think about it.",
      },
      {
        title: "5.0/5 from 38 Google reviews",
        description:
          "Punctuality, discretion and immaculate vehicles: the consistency that earns KS Rent Services a 5.0/5 rating from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during Les Voiles de Saint-Tropez",
    services: [
      {
        title: "Airport ↔ Saint-Tropez transfer",
        text: "Nice or Toulon-Hyères to Saint-Tropez, Ramatuelle and Gassin, with flight tracking.",
      },
      {
        title: "Old-port access",
        text: "Drop-off as close as possible to the quays and the regatta village, within the traffic and closures.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur to chain the port, lunches and prize-givings.",
      },
      {
        title: "Evenings & late-night returns",
        text: "Dinners in Saint-Tropez, Ramatuelle or Gassin, with a late return and a chauffeur who waits.",
      },
      {
        title: "Guest & crew shuttles",
        text: "Multi-vehicle coordination for guests, owners and crews.",
      },
    ],
  },
  locations: {
    heading: "Key points during Les Voiles de Saint-Tropez",
    items: [
      {
        name: "Old port of Saint-Tropez",
        description: "Quays, the Voiles village and the regatta starts.",
      },
      {
        name: "La Ponche & historic centre",
        description:
          "Lanes, squares and terraces in the heart of Saint-Tropez.",
      },
      {
        name: "Ramatuelle & Gassin",
        description: "Villas, estates and hotels across the peninsula.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Around a hundred kilometres away, Terminals 1 and 2.",
      },
      {
        name: "Sainte-Maxime & the gulf",
        description:
          "North shore of the gulf, an alternative for lodging and the sea shuttle.",
      },
    ],
  },
  generic: {
    heading: "Les Voiles de Saint-Tropez from a chauffeur's seat",
    subheading:
      "Every year in late September, the gulf fills with traditional yachts and the old port becomes the heart of the sailing world.",
    paragraphs: [
      "Every autumn, in late September and early October, Les Voiles de Saint-Tropez bring together classic and modern yachts in the gulf. The regatta village takes over the old port and the peninsula fills up: the single access road is saturated and parking becomes impossible.",
      "Most guests arrive via Nice Côte d'Azur airport, around a hundred kilometres away, or via Toulon-Hyères. With flight tracking and a name-board welcome, we absorb the peninsula's traffic jams to reach your hotel without stress.",
      "Between regattas, lunches at the port and evenings in Ramatuelle or Gassin, the pace is intense. Hourly hire keeps the same chauffeur all day and secures the late-night returns on a peninsula poorly served after midnight.",
    ],
    bullets: [
      "Old-port access",
      "Airport transfer ~100 km",
      "Late-night returns on the peninsula",
    ],
  },
  process: {
    heading: "Booking a chauffeur for Les Voiles de Saint-Tropez",
    steps: [
      "Tell us your regatta dates, your peninsula hotel and your timings.",
      "We plan the routes around the access traffic jams and port closures.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice or Toulon-Hyères arrivals.",
      "Direct transfer or hourly hire to cover the port, lunches and evenings.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur during Les Voiles de Saint-Tropez?",
      a: "Book early: in late September, the peninsula is saturated and demand is extreme. Share your dates, your hotel in Saint-Tropez, Ramatuelle or Gassin and your timings so we can secure a dedicated chauffeur.",
    },
    {
      q: "Can the chauffeur get me close to the old port during the regattas?",
      a: "Yes. The Voiles village occupies the old port and the surroundings are heavily congested. The chauffeur drops you at the nearest authorised point within the traffic, then collects you at the agreed spot.",
    },
    {
      q: "Do you handle the transfer from Nice airport?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur is around a hundred kilometres from Saint-Tropez; we anticipate the peninsula's traffic, and Toulon-Hyères can serve as a closer alternative.",
    },
    {
      q: "Is parking really impossible in Saint-Tropez?",
      a: "During Les Voiles, parking near the port is a feat and the car parks are full. The chauffeur removes the search entirely: he drops off, waits or returns at the agreed time.",
    },
    {
      q: "Do you cover late returns after the evenings?",
      a: "Yes. Dinners and evenings in Saint-Tropez, Ramatuelle or Gassin end late, and the peninsula is poorly served at night: the chauffeur waits and guarantees your direct return to the hotel.",
    },
    {
      q: "What vehicles do you offer?",
      a: "Saloons, SUVs and vans with a chauffeur depending on your group size, for a comfortable ride between the hotel, the port and the evenings.",
    },
    {
      q: "How long should I allow to reach the peninsula?",
      a: "It depends on traffic: in late September, reaching Saint-Tropez from Nice airport can be slow. We plan generously and follow the traffic in real time to avoid missing a regatta start or a dinner.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur hire Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    {
      label: "Private car service at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Book a chauffeur on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Chauffeur hire Cannes",
      href: "/en/chauffeur/cannes",
    },
  ],
  cta: {
    title: "Your chauffeur for Les Voiles de Saint-Tropez",
    text: "Airport transfer, old-port access and late-night returns: book a dedicated chauffeur for the regatta week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Les Voiles de Saint-Tropez Chauffeur | KS Rent",
    description:
      "Private chauffeur during Les Voiles de Saint-Tropez: Nice airport transfer, access to the old port despite the peninsula's traffic and late-night returns.",
    canonical: "/en/chauffeur/les-voiles-de-saint-tropez",
  },
};

export default lesVoilesDeSaintTropezEn;
